import { type BackupProgress, backupDue, dailyBackupDue, initialBackupProgress } from "@classroom/domain";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useCallback, useEffect, useRef, useState } from "react";
import type { BackupConfiguration } from "../../../../../supabase/functions/_shared/backupTypes";
import { digest } from "../../../../../supabase/functions/sync-control/policy";
import type { LocalAccount } from "../localStore/accountScope";
import { createBackupKeys, openBackupRecovery, sealBackupRecovery } from "../localStore/automaticBackupCrypto";
import { automaticBackupWorker, holdBackupActivity } from "../localStore/backupJob";
import { hasPendingLocalWrites } from "../localStore/client";
import { AutomaticBackup, type BackupControl, type BackupRecord } from "./automaticBackup";
import { GoogleBackupDrive } from "./backupDrive";
import { type BackupDevice, BackupVault } from "./backupVault";
import { SyncControlClient } from "./controlClient";
import { canonical, newDataKey, sealDeviceEnvelope, signChallenge } from "./cryptoV2";
import { syncReviewId } from "./reviewScope";
import { dataKeyScope } from "./syncScopes";
import type { useSyncReview } from "./useSyncReview";

type BackupAccountState = {
  config: BackupConfiguration | null;
  drive: { subject: string; displayEmail: string } | null;
  oauthCandidate: { subject: string; displayEmail: string } | null;
};
type Status = BackupAccountState & {
  ready: boolean;
  unlocked: boolean;
  busy: boolean;
  error: string;
  notice: boolean;
  records: BackupRecord[];
  progress: BackupProgress;
  recoveryVerified: boolean;
  restored: boolean;
  recoveryPending: boolean;
};
function backupIdentity({
  accountId,
  deviceId,
  signing,
  agreement,
  wrapping,
}: Omit<BackupDevice["identity"], "label">): BackupDevice["identity"] {
  return { accountId, deviceId, signing, agreement, wrapping, label: navigator.platform || "Browser" };
}
export function useAutomaticBackup(
  account: LocalAccount,
  auth: SupabaseClient,
  sync: ReturnType<typeof useSyncReview>,
  assertAccess?: () => void,
) {
  const [status, setStatus] = useState<Status>({
    config: null,
    drive: null,
    oauthCandidate: null,
    ready: false,
    unlocked: false,
    busy: false,
    error: "",
    notice: false,
    records: [],
    progress: initialBackupProgress(),
    recoveryVerified: false,
    restored: false,
    recoveryPending: false,
  });
  const state = useRef(status);
  state.current = status;
  const runtime = useRef<{
    vault: BackupVault;
    saved: BackupDevice;
    api: SyncControlClient;
    control: BackupControl;
  } | null>(null);
  const running = useRef(false),
    live = useRef(true),
    retryAt = useRef(0),
    attempts = useRef(0);
  const syncRef = useRef(sync);
  syncRef.current = sync;
  const accessRef = useRef(assertAccess);
  accessRef.current = assertAccess;
  const abort = useRef<AbortController | null>(null);
  const refreshedAt = useRef(0);
  const lockName = `classroom-backup:${account.workspaceId}`;
  const update = useCallback((patch: Partial<Status>) => {
    if (live.current)
      setStatus((old) => {
        const next = { ...old, ...patch };
        state.current = next;
        return next;
      });
  }, []);
  const persist = async () => {
    const r = runtime.current;
    if (!r) throw new Error("sync-backup-not-ready");
    await r.vault.write(account.userId, account.workspaceId, r.saved);
  };
  const context = () => {
    const r = runtime.current,
      client = syncRef.current.client;
    if (!r || !client) throw new Error("sync-backup-not-ready");
    return { ...r, client };
  };
  const bindDevice = (vault: BackupVault, saved: BackupDevice) => {
    const endpoint =
      import.meta.env.VITE_SYNC_CONTROL_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-control`;
    const api = new SyncControlClient(auth, endpoint, saved.identity);
    const control: BackupControl = {
      request: async <T>(action: string, payload: Record<string, unknown> = {}) =>
        api.request<T>(action, { device: await api.publicDevice(), ...payload }),
    };
    return { vault, saved, api, control };
  };
  const drive = async (signal?: AbortSignal) => {
    const r = context();
    let cached: { accessToken: string; expiresAt: number } | undefined;
    return new GoogleBackupDrive(
      async () => {
        if (!cached || cached.expiresAt < Date.now() + 60000) cached = await r.control.request("backup-drive-token");
        return cached!.accessToken;
      },
      await digest([account.userId, account.workspaceId]),
      signal,
    );
  };
  const engine = async () => {
    const r = context();
    if (!r.saved.key || r.saved.keyId !== state.current.config?.keyId) throw new Error("sync-backup-device-required");
    const controller = new AbortController();
    abort.current = controller;
    return new AutomaticBackup({
      workspaceId: account.workspaceId,
      deviceId: r.saved.identity.deviceId,
      key: r.saved.key,
      crypto: automaticBackupWorker,
      control: r.control,
      drive: await drive(controller.signal),
      abort: () => controller.abort(),
      tasks: {
        read: async () => r.saved.job,
        write: async (job) => {
          r.saved.job = job;
          await persist();
        },
      },
      local: {
        capture: () => r.client.captureAutomaticBackup(account.workspaceId),
        validate: async (source) => {
          await r.client.validateBackupImage(
            account.workspaceId,
            source.snapshot,
            source.sqliteImage!,
            source.schemaVersion,
          );
          await r.client.validateSnapshot(account.workspaceId, source.snapshot);
        },
        acknowledge: (revision, time, restoreId) =>
          r.client.acknowledgeAutomaticBackup(account.workspaceId, revision, time, restoreId),
        restore: async (snapshot, expectedHash) => {
          accessRef.current?.();
          const restoreId = crypto.randomUUID();
          if (!syncRef.current.status.local) {
            r.saved.restoreIntent = restoreId;
            await persist();
          }
          await r.client.restoreAutomaticBackup(account.workspaceId, snapshot, expectedHash, restoreId);
          update({ restored: true, recoveryPending: !!r.saved.restoreIntent });
        },
      },
      assertRestore: () => {
        accessRef.current?.();
        if (
          hasPendingLocalWrites() ||
          document.querySelector('[data-save-state="dirty"], [data-save-state="saving"], [data-save-state="error"]')
        )
          throw new Error("sync-draft-save-required");
        if (syncRef.current.status.local) syncRef.current.assertClassroom();
      },
      afterRestore: async () => {
        // A clean-device recovery adopts a new generation separately; existing
        // devices keep their causal history and publish replacement operations.
        if (syncRef.current.status.local) await syncRef.current.run();
        else await recoverConnection();
      },
    });
  };
  const recoverConnection = async () => {
    const r = context(),
      sync = syncRef.current;
    const progress = await r.client.backupProgress(account.workspaceId);
    if (!r.saved.restoreIntent || progress.lastRestoreId !== r.saved.restoreIntent)
      throw new Error("sync-backup-recovery-invalid");
    let prepared = r.saved.recoveryRoot;
    if (!prepared) {
      const scope = {
        datasetId: sync.status.summary?.root?.datasetId ?? crypto.randomUUID(),
        generation: crypto.randomUUID(),
        keyVersion: 1,
      };
      const material = newDataKey();
      const publicDevice = await r.api.publicDevice();
      const root = {
        ...scope,
        recoveryMode: "device" as const,
        recoveryPublicKey: publicDevice.signing,
        deviceEnvelopes: {
          [r.saved.identity.deviceId]: await sealDeviceEnvelope(
            publicDevice.agreement,
            { ...dataKeyScope(scope), objectId: r.saved.identity.deviceId },
            { dataKey: material },
          ),
        },
      };
      await sync.vault.prepareSetup(
        { accountId: account.userId, deviceId: r.saved.identity.deviceId },
        dataKeyScope(scope),
        material,
        r.saved.identity,
      );
      prepared = { root, expectedGeneration: sync.status.summary?.root?.generation ?? null };
      r.saved.recoveryRoot = prepared;
      await persist();
    }
    update({ recoveryPending: true });
    const pending = await sync.vault.pendingSetup(account.userId);
    if (
      !pending ||
      pending.deviceId !== r.saved.identity.deviceId ||
      pending.scope.generation !== prepared.root.generation
    )
      throw new Error("sync-backup-recovery-invalid");
    await r.control.request("backup-recover-root", { ...prepared, confirmed: true });
    await r.client.syncCreateLocal(
      account.workspaceId,
      {
        datasetId: pending.scope.datasetId,
        generation: pending.scope.generation,
        keyVersion: pending.scope.keyVersion,
        deviceId: pending.deviceId,
      },
      pending.dataKey,
    );
    await sync.vault.finishSetup(account.userId);
    r.saved.recoveryRoot = undefined;
    r.saved.restoreIntent = undefined;
    await persist();
    update({ recoveryPending: false, restored: true });
  };
  const refresh = async () => {
    const r = context();
    const info = await r.control.request<BackupAccountState>("backup-state");
    if (info.config && info.config.workspaceId !== account.workspaceId) throw new Error("sync-backup-scope");
    const unlocked = !!r.saved.key && r.saved.keyId === info.config?.keyId;
    const progress = await r.client.backupProgress(account.workspaceId);
    const recoveryPending = !!r.saved.restoreIntent && progress.lastRestoreId === r.saved.restoreIntent;
    refreshedAt.current = Date.now();
    update({
      ...info,
      unlocked,
      ready: true,
      recoveryPending,
      restored: state.current.restored || recoveryPending,
      progress,
    });
    if (unlocked) {
      const value = await (await engine()).catalog();
      update({ records: value.records, recoveryVerified: value.catalog.recoveryVerified });
    }
  };
  const report = async (error: unknown) => {
    const code = error instanceof Error && /^sync-[a-z-]+$/.test(error.message) ? error.message : "sync-backup-failed";
    if (["sync-backup-busy", "sync-backup-cancelled"].includes(code)) return;
    attempts.current++;
    retryAt.current = Date.now() + Math.min(15 * 60000, 15000 * 2 ** Math.min(attempts.current, 6));
    const r = runtime.current;
    const pending = state.current.progress.pendingSince;
    const actionable =
      ["sync-google-reconnect", "sync-drive-quota", "sync-drive-denied", "sync-backup-device-required"].includes(
        code,
      ) ||
      (pending !== null && Date.now() - pending >= 24 * 3600000);
    const notice = `${code}:${pending ?? ""}`;
    const fresh = actionable && r?.saved.notice !== notice;
    if (r && fresh) {
      r.saved.notice = notice;
      await persist().catch(() => undefined);
    }
    update({ error: code, notice: fresh });
  };
  const perform = async <T>(action: () => Promise<T>, clearsFailure = true): Promise<T | undefined> => {
    if (running.current) return;
    running.current = true;
    update({ busy: true });
    const releaseActivity = holdBackupActivity();
    try {
      const result = await navigator.locks.request(lockName, { ifAvailable: true }, async (lock) => {
        if (!lock) throw new Error("sync-backup-busy");
        const r = runtime.current;
        if (r) r.saved = (await r.vault.read(account.userId, account.workspaceId)) ?? r.saved;
        return action();
      });
      if (clearsFailure) {
        attempts.current = 0;
        retryAt.current = 0;
        update({ error: "", notice: false });
        if (runtime.current) {
          runtime.current.saved.notice = undefined;
          await persist();
        }
      }
      await refresh();
      return result;
    } catch (error) {
      await report(error);
    } finally {
      releaseActivity();
      abort.current = null;
      running.current = false;
      update({ busy: false });
    }
  };
  const finishSetup = async () => {
    const r = context();
    const info = await r.control.request<BackupAccountState>("backup-state");
    if (!info.config || !info.drive) {
      update(info);
      return;
    }
    update(info);
    const storage = await drive();
    const catalog = await (await engine()).catalog();
    if (!r.saved.setup && catalog.catalog.recoveryVerified && catalog.records.length && info.config.recoveryFileId) {
      // Reauthorizing Drive does not change a teacher's existing opt-out.
      if (canonical(await storage.read(info.config.recoveryFileId)) !== canonical(info.config.recovery))
        throw new Error("sync-backup-recovery-invalid");
      return;
    }
    const fileId = info.config.recoveryFileId || r.saved.setup?.recoveryFileId || (await storage.allocateId());
    r.saved.setup = { ...info.config, recoveryFileId: fileId };
    await persist();
    const existing = await storage.read(fileId);
    if (!existing) {
      try {
        await storage.create(fileId, info.config.recovery, "recovery");
      } catch (error) {
        if (!(error instanceof Error) || error.message !== "sync-drive-exists") throw error;
      }
    }
    if (canonical(await storage.read(fileId)) !== canonical(info.config.recovery))
      throw new Error("sync-backup-recovery-invalid");
    await r.control.request("backup-key-verified", { fileId });
    await (await engine()).run();
    await r.control.request("backup-setting", { enabled: true });
    r.saved.setup = undefined;
    await persist();
  };
  const initialize = (password: string) =>
    perform(async () => {
      let r = context();
      const current = syncRef.current.device.current;
      if (current && current.deviceId !== r.saved.identity.deviceId) {
        const info = await r.control.request<BackupAccountState>("backup-state");
        if (!info.config) {
          // A teacher may pair after opening the app. Use that authorized
          // identity only before enrollment; retain any prepared backup keys.
          r.saved.identity = backupIdentity(current);
          await persist();
          runtime.current = bindDevice(r.vault, r.saved);
          r = context();
        }
      }
      let config = r.saved.setup;
      if (config) {
        // A retry must not silently accept a different password while keeping
        // the recovery package prepared by the first attempt.
        try {
          await openBackupRecovery(config.recovery, password, account.userId, account.workspaceId);
        } catch {
          throw new Error("sync-backup-setup-password-mismatch");
        }
      } else {
        const keys = await createBackupKeys();
        config = {
          workspaceId: account.workspaceId,
          keyId: keys.keyId,
          recovery: await sealBackupRecovery(keys, password, account.userId, account.workspaceId),
          recoveryPublicKey: keys.recoveryPublic,
          recoveryFileId: "",
          enabled: false,
        };
        r.saved.keyId = keys.keyId;
        r.saved.key = keys.key;
        r.saved.setup = config;
        await persist();
      }
      await r.control.request("backup-init", { config: { ...config, recoveryFileId: "", enabled: false } });
      update({ config, unlocked: true });
      await finishSetup();
    });
  const unlock = (password: string) =>
    perform(async () => {
      const r = context(),
        config = state.current.config;
      if (!config) throw new Error("sync-backup-not-enabled");
      const keys = await openBackupRecovery(config.recovery, password, account.userId, account.workspaceId);
      const challenge = await r.control.request<{ challengeId: string; challenge: unknown }>("backup-challenge");
      const recovery = await crypto.subtle.importKey(
        "jwk",
        keys.recoveryPrivate,
        { name: "ECDSA", namedCurve: "P-256" },
        false,
        ["sign"],
      );
      await r.control.request("backup-unlock", {
        challengeId: challenge.challengeId,
        signature: await signChallenge(recovery, challenge.challenge),
      });
      r.saved.key = keys.key;
      r.saved.keyId = keys.keyId;
      await persist();
      update({ unlocked: true });
      if (
        config.recoveryFileId &&
        state.current.drive &&
        canonical(await (await drive()).read(config.recoveryFileId)) !== canonical(config.recovery)
      )
        throw new Error("sync-backup-recovery-invalid");
    });
  const reportRef = useRef(report);
  reportRef.current = report;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const tickRef = useRef(async () => {});
  tickRef.current = async () => {
    if (!runtime.current || !syncRef.current.client || running.current || document.hidden) return;
    const progress = await syncRef.current.client.backupProgress(account.workspaceId);
    update({ progress });
    if (
      state.current.config?.enabled &&
      progress.pendingSince !== null &&
      Date.now() - progress.pendingSince >= 24 * 3600000 &&
      !state.current.error
    )
      await report(new Error("sync-backup-overdue"));
    if (!navigator.onLine || Date.now() < retryAt.current) return;
    if (Date.now() - refreshedAt.current >= 60000) await perform(refresh, false);
    if (!state.current.unlocked || !state.current.config?.enabled || state.current.recoveryPending) return;
    if (
      runtime.current.saved.job ||
      backupDue(progress, Date.now()) ||
      dailyBackupDue(progress.lastCheckedAt, Date.now())
    )
      await perform(async () => {
        const info = await context().control.request<BackupAccountState>("backup-state");
        update(info);
        if (info.config?.enabled) await (await engine()).run();
      });
  };
  useEffect(() => {
    if (!sync.status.ready || !sync.client) return;
    live.current = true;
    let canceled = false;
    void navigator.locks
      .request(lockName, async () => {
        const vault = new BackupVault(`classroom-automatic-backup-${syncReviewId() ?? "app"}`);
        const current = syncRef.current.device.current;
        const saved = await vault.identity(
          account.userId,
          account.workspaceId,
          current ? backupIdentity(current) : undefined,
        );
        if (canceled) return;
        runtime.current = bindDevice(vault, saved);
        await refreshRef.current();
      })
      .then(() => {
        if (!canceled) void tickRef.current().catch((error) => reportRef.current(error));
      })
      .catch((error) => reportRef.current(error));
    const tick = () => {
      void tickRef.current().catch((error) => reportRef.current(error));
    };
    const foreground = () => {
      retryAt.current = 0;
      refreshedAt.current = 0;
      tick();
    };
    const timer = setInterval(tick, 15000);
    window.addEventListener("online", foreground);
    document.addEventListener("visibilitychange", foreground);
    return () => {
      canceled = true;
      live.current = false;
      clearInterval(timer);
      window.removeEventListener("online", foreground);
      document.removeEventListener("visibilitychange", foreground);
      abort.current?.abort();
      runtime.current = null;
    };
  }, [account.workspaceId, account.userId, auth, sync.status.ready, sync.client, lockName]);
  return {
    status,
    initialize,
    unlock,
    recoverConnection: () =>
      perform(async () => {
        await recoverConnection();
        await (await engine()).run();
      }),
    refresh: () => perform(refresh, false),
    resume: () => perform(finishSetup),
    backupNow: () =>
      perform(async () => {
        if (
          hasPendingLocalWrites() ||
          document.querySelector('[data-save-state="dirty"], [data-save-state="saving"], [data-save-state="error"]')
        )
          throw new Error("sync-draft-save-required");
        await (await engine()).run();
      }),
    toggle: (enabled: boolean) =>
      perform(async () => {
        await context().control.request("backup-setting", { enabled });
      }),
    inspect: (id: string) => perform(async () => (await engine()).inspect(id), false),
    restore: (id: string) =>
      perform(async () => {
        await (await engine()).restore(id);
      }),
    connect: () =>
      perform(async () =>
        context().control.request<{ url: string }>("backup-oauth-start", {
          returnOrigin: location.origin,
          returnPath: syncReviewId() ? "/sync-review" : "/app",
        }),
      ),
    confirmDrive: (subject: string) =>
      perform(async () => {
        await context().control.request("backup-oauth-complete", { confirmedSubject: subject });
        await finishSetup();
      }),
    dismissNotice: () => update({ notice: false }),
  };
}
